import React, { useState, useRef } from 'react';
import { ImageIcon, X, Upload, Link2 } from 'lucide-react';
import { adminUploads, adminProducts } from '../../services/api';
import api from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ msg }) {
  return (
    <div
      className="toast-container position-fixed bottom-0 end-0 p-3"
      style={{ zIndex: 9999 }}
    >
      <div className="toast show align-items-center text-bg-success border-0">
        <div className="d-flex">
          <div className="toast-body">{msg}</div>
        </div>
      </div>
    </div>
  );
}

// ── ImageUploadModal ──────────────────────────────────────────────────────────
// Props:
//   product   — product object (productID, proFanciful, proVintage, proLabelImageUrl)
//   onClose() — close the modal
//   onSaved(url | null) — called with the new image URL after upload, or null after removal
export default function ImageUploadModal({ product, onClose, onSaved }) {
  const [previewUrl, setPreviewUrl]           = useState(imgSrc(product.proLabelImageUrl));
  const [selectedFile, setSelectedFile]       = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [uploading, setUploading]             = useState(false);
  const [progress, setProgress]               = useState(0);
  const [removing, setRemoving]               = useState(false);
  const [isDragging, setIsDragging]           = useState(false);
  const [toastMsg, setToastMsg]               = useState('');

  // URL mode state
  const [mode, setMode]                       = useState(isMobile ? 'url' : 'upload');
  const [urlInput, setUrlInput]               = useState('');
  const [urlPreviewError, setUrlPreviewError] = useState(false);
  const [urlSaving, setUrlSaving]             = useState(false);
  const [urlSaveError, setUrlSaveError]       = useState('');

  const inputRef   = useRef(null);
  const toastTimer = useRef(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 4000);
  };

  // ── File selection ──────────────────────────────────────────────────────────
  const handleFile = (file) => {
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setSelectedPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  // ── Upload ──────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!selectedFile || uploading) return;
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const res = await api.post(
        `/api/admin/uploads/products/${product.productID}`,
        formData,
        { onUploadProgress: (e) => setProgress(Math.round((e.loaded / e.total) * 100)) }
      );

      if (res.data?.success) {
        const rawUrl = res.data.data.imageUrl;
        setPreviewUrl(imgSrc(rawUrl));
        setSelectedFile(null);
        setSelectedPreview(null);
        setProgress(0);
        onSaved(rawUrl);
        showToast('Image saved');
      }
    } catch {
      // silent — user can retry
    } finally {
      setUploading(false);
    }
  };

  // ── Remove ──────────────────────────────────────────────────────────────────
  const handleRemove = async () => {
    setRemoving(true);
    try {
      await adminUploads.deleteProductImage(product.productID);
      setPreviewUrl(null);
      setSelectedFile(null);
      setSelectedPreview(null);
      onSaved(null);
    } catch {
      // silent
    } finally {
      setRemoving(false);
    }
  };

  // ── Save URL ─────────────────────────────────────────────────────────────────
  const handleSaveUrl = async () => {
    if (!urlInput || urlPreviewError || urlSaving) return;
    setUrlSaving(true);
    setUrlSaveError('');
    try {
      const res = await adminProducts.update(product.productID, { proLabelImageUrl: urlInput.trim() });
      if (res.data?.success) {
        onClose();
        onSaved(urlInput);
      } else {
        setUrlSaveError('Failed to save URL. Please try again.');
      }
    } catch (err) {
      setUrlSaveError(err?.response?.data?.message || 'Failed to save URL. Please try again.');
    } finally {
      setUrlSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">

            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                Product Image — {product.proFanciful} {product.proVintage}
              </h5>
              <button className="btn-close" onClick={onClose} />
            </div>

            {/* Body */}
            <div className="modal-body">

              {/* Mode toggle — desktop only */}
              {!isMobile && (
                <div className="d-flex mb-3">
                  <div className="btn-group btn-group-sm" role="group" aria-label="Image source mode">
                    <button
                      type="button"
                      className={`btn ${mode === 'upload' ? 'btn-dark' : 'btn-outline-secondary'}`}
                      onClick={() => setMode('upload')}
                    >
                      <Upload size={13} className="me-1" />Upload File
                    </button>
                    <button
                      type="button"
                      className={`btn ${mode === 'url' ? 'btn-dark' : 'btn-outline-secondary'}`}
                      onClick={() => setMode('url')}
                    >
                      <Link2 size={13} className="me-1" />Use URL
                    </button>
                  </div>
                </div>
              )}

              <div className="row g-4">

                {/* Left: current image preview */}
                <div className="col-md-5">
                  <p className="small fw-semibold mb-2 text-muted">Current image</p>
                  <div
                    className="d-flex align-items-center justify-content-center rounded"
                    style={{ height: 300, background: '#f8f8f8' }}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Current label"
                        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <div className="text-center text-muted">
                        <ImageIcon size={48} className="mb-2 opacity-25" />
                        <p className="small mb-0">No image</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: upload or URL controls */}
                <div className="col-md-7 d-flex flex-column">

                  {mode === 'upload' ? (
                    <>
                      <p className="small fw-semibold mb-2 text-muted">Upload new image</p>

                      {/* Drop zone */}
                      <div
                        className={`rounded mb-2 text-center d-flex flex-column align-items-center justify-content-center${isDragging ? ' border-primary' : ''}`}
                        style={{
                          border: `2px dashed ${isDragging ? '#0d6efd' : '#ccc'}`,
                          background: isDragging ? 'rgba(13,110,253,.05)' : '#fafafa',
                          cursor: 'pointer',
                          minHeight: 150,
                          padding: '1.5rem',
                          transition: 'border-color .15s, background .15s',
                        }}
                        onClick={() => inputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                      >
                        {selectedFile ? (
                          <>
                            {selectedPreview && (
                              <img
                                src={selectedPreview}
                                alt="Preview"
                                style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain', marginBottom: 8, borderRadius: 4 }}
                              />
                            )}
                            <div className="d-flex align-items-center gap-1">
                              <span className="small text-truncate" style={{ maxWidth: 200 }}>
                                {selectedFile.name}
                              </span>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-muted"
                                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setSelectedPreview(null); }}
                              >
                                <X size={13} />
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <ImageIcon size={32} className="text-muted mb-2 opacity-50" />
                            <p className="small mb-0 text-muted">
                              Drag & drop or <span className="text-primary">click to browse</span>
                            </p>
                          </>
                        )}
                        <input
                          ref={inputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="d-none"
                          onChange={(e) => handleFile(e.target.files[0])}
                        />
                      </div>

                      <p className="text-muted mb-3" style={{ fontSize: 11 }}>
                        JPEG, PNG or WebP · max 5 MB · portrait orientation recommended
                      </p>

                      {/* Progress bar */}
                      {uploading && (
                        <div className="progress mb-3" style={{ height: 6 }}>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${progress}%` }}
                            aria-valuenow={progress}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                      )}

                      {/* Upload button */}
                      <button
                        className="btn btn-primary btn-sm d-flex align-items-center justify-content-center gap-2"
                        disabled={!selectedFile || uploading}
                        onClick={handleUpload}
                      >
                        {uploading && (
                          <span className="spinner-border spinner-border-sm" role="status" />
                        )}
                        {uploading ? `Uploading… ${progress}%` : 'Upload'}
                      </button>

                      {/* Remove link */}
                      {previewUrl && (
                        <button
                          className="btn btn-link btn-sm text-danger mt-3 p-0 align-self-start"
                          disabled={removing}
                          onClick={handleRemove}
                        >
                          {removing && (
                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                          )}
                          Remove image
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="small fw-semibold mb-2 text-muted">Use image URL</p>
                      <input
                        type="url"
                        className="form-control form-control-sm mb-3"
                        placeholder="https://example.com/label.jpg"
                        value={urlInput}
                        onChange={(e) => {
                          setUrlInput(e.target.value);
                          setUrlPreviewError(false);
                        }}
                      />
                      {urlInput && (
                        urlPreviewError ? (
                          <div className="alert alert-warning py-2 small mb-0">
                            Image could not be loaded from this URL. Check the address and try again.
                          </div>
                        ) : (
                          <img
                            src={urlInput}
                            alt="URL preview"
                            onError={() => setUrlPreviewError(true)}
                            style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                          />
                        )
                      )}
                      {urlSaveError && (
                        <div className="alert alert-danger py-2 small mt-3 mb-0">{urlSaveError}</div>
                      )}
                    </>
                  )}

                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              {mode === 'upload' ? (
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  disabled={!selectedFile || uploading}
                  onClick={handleUpload}
                >
                  {uploading && <span className="spinner-border spinner-border-sm" role="status" />}
                  {uploading ? `Uploading… ${progress}%` : 'Upload'}
                </button>
              ) : (
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  disabled={!urlInput || urlPreviewError || urlSaving}
                  onClick={handleSaveUrl}
                >
                  {urlSaving && <span className="spinner-border spinner-border-sm" role="status" />}
                  Save URL
                </button>
              )}
            </div>

          </div>
        </div>
      </div>

      {toastMsg && <Toast msg={toastMsg} />}
    </>
  );
}
